import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Buildings, Plus, DotsThree, Pencil, Trash, Envelope, Phone, MapPin, Spinner, Camera, User } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const initialFormState = {
    name: '',
    company: '',
    email: '',
    phone: '',
    address: ''
};

function getAvatarUrl(path) {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/storage/${path}`;
}

function BrokerAvatar({ broker, size = 'sm' }) {
    const dim = size === 'sm' ? 'w-9 h-9' : 'w-20 h-20';
    const textSize = size === 'sm' ? 'text-sm' : 'text-2xl';
    const url = getAvatarUrl(broker?.avatar);

    return (
        <div className={`${dim} rounded-full overflow-hidden bg-muted flex items-center justify-center border border-border shrink-0`}>
            {url ? (
                <img src={url} alt={broker?.name} className="w-full h-full object-cover" />
            ) : (
                <span className={`${textSize} font-medium text-muted-foreground`}>
                    {broker?.name?.charAt(0)?.toUpperCase() || <User size={size === 'sm' ? 16 : 32} />}
                </span>
            )}
        </div>
    );
}

export default function BrokersPage() {
    const [brokers, setBrokers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [editId, setEditId] = useState(null);
    const [editAvatar, setEditAvatar] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const fileInputRef = useRef(null);

    const fetchBrokers = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/brokers`, { withCredentials: true });
            setBrokers(data);
        } catch (error) {
            toast.error('Fehler beim Laden der Makler');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBrokers();
    }, [fetchBrokers]);

    const handleOpenDialog = (broker = null) => {
        if (broker) {
            setEditId(broker.id);
            setEditAvatar(broker.avatar || null);
            setFormData({
                name: broker.name || '',
                company: broker.company || '',
                email: broker.email || '',
                phone: broker.phone || '',
                address: broker.address || ''
            });
        } else {
            setEditId(null);
            setEditAvatar(null);
            setFormData(initialFormState);
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            if (editId) {
                await axios.put(`${API}/api/brokers/${editId}`, formData, { withCredentials: true });
                toast.success('Makler aktualisiert');
            } else {
                await axios.post(`${API}/api/brokers`, formData, { withCredentials: true });
                toast.success('Makler erstellt');
            }
            setDialogOpen(false);
            fetchBrokers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
        setSubmitting(false);
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !editId) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Maximale Dateigröße: 5 MB');
            return;
        }

        setUploadingAvatar(true);
        const fd = new FormData();
        fd.append('file', file);

        try {
            const { data } = await axios.post(`${API}/api/brokers/${editId}/avatar`, fd, { withCredentials: true });
            setEditAvatar(data.avatar);
            toast.success('Profilbild aktualisiert');
            fetchBrokers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
        }
        setUploadingAvatar(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDeleteAvatar = async () => {
        if (!editId) return;
        try {
            await axios.delete(`${API}/api/brokers/${editId}/avatar`, { withCredentials: true });
            setEditAvatar(null);
            toast.success('Profilbild entfernt');
            fetchBrokers();
        } catch (error) {
            toast.error('Fehler beim Entfernen');
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${API}/api/brokers/${deleteId}`, { withCredentials: true });
            toast.success('Makler gelöscht');
            fetchBrokers();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
        setDeleteId(null);
    };

    return (
        <div className="space-y-6" data-testid="brokers-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Makler
                </h1>
                <Button className="rounded-sm" onClick={() => handleOpenDialog()} data-testid="add-broker-btn">
                    <Plus size={16} className="mr-2" />
                    Makler hinzufügen
                </Button>
            </div>

            <Card className="border" data-testid="brokers-table">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F8F9FA]">
                            <TableHead className="font-semibold w-12"></TableHead>
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">Unternehmen</TableHead>
                            <TableHead className="font-semibold">E-Mail</TableHead>
                            <TableHead className="font-semibold">Telefon</TableHead>
                            <TableHead className="font-semibold">Adresse</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <TableRow key={i}>
                                    {[...Array(7)].map((_, j) => (
                                        <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : brokers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                                    <Buildings size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>Keine Makler vorhanden</p>
                                    <Button variant="link" onClick={() => handleOpenDialog()} className="mt-2">
                                        Ersten Makler hinzufügen
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ) : (
                            brokers.map((broker) => (
                                <TableRow key={broker.id} className="table-row-hover" data-testid={`broker-row-${broker.id}`}>
                                    <TableCell>
                                        <BrokerAvatar broker={broker} size="sm" />
                                    </TableCell>
                                    <TableCell className="font-medium">{broker.name}</TableCell>
                                    <TableCell>{broker.company || '-'}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                            <Envelope size={14} />
                                            <span>{broker.email}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {broker.phone ? (
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <Phone size={14} />
                                                <span>{broker.phone}</span>
                                            </div>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {broker.address ? (
                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                <MapPin size={14} />
                                                <span className="truncate max-w-[200px]">{broker.address}</span>
                                            </div>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`broker-menu-${broker.id}`}>
                                                    <DotsThree size={16} weight="bold" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem className="cursor-pointer" onClick={() => handleOpenDialog(broker)}>
                                                    <Pencil size={14} className="mr-2" />
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuItem className="text-destructive cursor-pointer" onClick={() => setDeleteId(broker.id)}>
                                                    <Trash size={14} className="mr-2" />
                                                    Löschen
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                            {editId ? 'Makler bearbeiten' : 'Neuen Makler hinzufügen'}
                        </DialogTitle>
                        <DialogDescription>
                            Geben Sie die Maklerdaten ein
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            {/* Avatar Upload (only when editing) */}
                            {editId && (
                                <div className="flex items-center gap-4" data-testid="broker-avatar-section">
                                    <div className="relative group">
                                        <BrokerAvatar broker={{ name: formData.name, avatar: editAvatar }} size="lg" />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="absolute inset-0 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                            disabled={uploadingAvatar}
                                            data-testid="broker-avatar-upload-trigger"
                                        >
                                            {uploadingAvatar ? (
                                                <Spinner className="animate-spin" size={20} />
                                            ) : (
                                                <Camera size={20} />
                                            )}
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            onChange={handleAvatarUpload}
                                            className="hidden"
                                            data-testid="broker-avatar-file-input"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="rounded-sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingAvatar}
                                            data-testid="broker-change-avatar-btn"
                                        >
                                            <Camera size={14} className="mr-1" />
                                            {editAvatar ? 'Bild ändern' : 'Bild hochladen'}
                                        </Button>
                                        {editAvatar && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="rounded-sm text-destructive hover:text-destructive"
                                                onClick={handleDeleteAvatar}
                                                data-testid="broker-delete-avatar-btn"
                                            >
                                                <Trash size={14} className="mr-1" />
                                                Bild entfernen
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    className="rounded-sm"
                                    data-testid="broker-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="company">Unternehmen</Label>
                                <Input
                                    id="company"
                                    value={formData.company}
                                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                    className="rounded-sm"
                                    data-testid="broker-company-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    className="rounded-sm"
                                    data-testid="broker-email-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefon</Label>
                                <Input
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="rounded-sm"
                                    data-testid="broker-phone-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="address">Adresse</Label>
                                <Input
                                    id="address"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    className="rounded-sm"
                                    data-testid="broker-address-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-sm" data-testid="broker-submit-btn">
                                {submitting ? (
                                    <>
                                        <Spinner className="animate-spin mr-2" size={16} />
                                        Speichern...
                                    </>
                                ) : (
                                    'Speichern'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Makler löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Diese Aktion kann nicht rückgängig gemacht werden. Der Makler wird dauerhaft gelöscht.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
